import montserratBold from '@assets/fonts/Montserrat-Bold.js';
import montserratExtaBold from '@assets/fonts/Montserrat-ExtraBold.js';
import montserratMedium from '@assets/fonts/Montserrat-Medium.js';
import Border from '@assets/images/tip_direct/Border.png';
import QRHolder from '@assets/images/tip_direct/QRHolder.jpg';
import TipDirect from '@assets/images/tip_direct/TipDirect.png';
import TrustBuilders from '@assets/images/tip_direct/TrustBuilders.png';
import { regionalURLs } from '@utils/constants';
import { getCompanyLogoUrl } from '@utils/helpers';
import { jsPDF } from 'jspdf';
import JSZip from 'jszip';
import QRCode from 'qrcode';

// Generate QR code URL based on the user's region
const generateQrURL = (userId, region) => {
  return (regionalURLs[region] || regionalURLs.US) + `t/g/${userId}`;
};

// Generate QR code image element
const generateQrImage = async qrURL => {
  try {
    const qrCode = await QRCode.toDataURL(qrURL);
    const qrImg = new Image();
    qrImg.src = qrCode;

    return qrImg;
  } catch (err) {
    console.error('Failed to generate QR code:', err);
    return null;
  }
};

// Add QR code image
const addQrCodeToPDF = (doc, qrImg) => {
  doc.addImage(QRHolder, 49.95, 5.16, 29.72, 44.96);
  if (!qrImg) return;
  const x = 49.95 + 1.36;
  const y = 5.16 + 1.36;
  doc.addImage(qrImg, 'png', x, y, 27, 27);
};

//Add company logo
const addCompanyLogoToPDF = async (doc, companyLogo) => {
  if (!companyLogo) {
    return;
  }

  const logoSrc = getCompanyLogoUrl(companyLogo);

  const logo = await new Promise((resolve, reject) => {
    const img = new Image();
    img.src = logoSrc;
    img.onload = () => resolve(img);
    img.onerror = () => {
      reject(new Error('Invalid image URL'));
    };
  }).catch(error => {
    console.error(error);
  });

  if (!logo) {
    return;
  }

  //Original aspect ratio of the image
  const aspectRatio = logo.width / logo.height;
  const maxWidth = 66.91;
  const maxHeight = 35.56;

  //Maintains aspect ratio while resizing and ensures image doesn't overflow in either axis
  let logoWidth, logoHeight;
  if (logo.width > logo.height) {
    logoWidth = maxWidth;
    logoHeight = logoWidth / aspectRatio;

    if (logoHeight > maxHeight) {
      logoHeight = maxHeight;
      logoWidth = maxHeight * aspectRatio;
    }
  } else {
    logoHeight = maxHeight;
    logoWidth = logoHeight * aspectRatio;

    if (logoWidth > maxWidth) {
      logoWidth = maxWidth;
      logoHeight = maxWidth / aspectRatio;
    }
  }

  const x = 9.14 + (66.91 - logoWidth) / 2;
  const y = 9.82 + (35.56 - logoHeight) / 2;
  doc.addImage(logo, 'png', x, y, logoWidth, logoHeight);
};

// Add background shape
const addQRHolderToPDF = doc => {
  doc.addImage(QRHolder, 49.95, 5.16, 29.72, 44.96);
};

// Add static images
const addStaticImagesToPDF = doc => {
  doc.addImage(TrustBuilders, 53.2, 41.59, 23.23, 6.43);
  doc.addImage(TipDirect, 52.28, 36.26, 25.06, 5.34);
};

// Add text
const addTextToPDF = (doc, firstName, lastName, nickname = null) => {
  // Heading text
  doc.setFont('Montserrat-Bold', 'normal');
  doc.setFontSize(12.96);
  doc.text('Thank you for joining the tour!', 5.84, 30.9, {
    align: 'left',
    maxWidth: 42.33
  });

  doc.setFont('Montserrat-Medium', 'normal');
  doc.setFontSize(9.26);
  doc.text('All reviews and gratuities are greatly appreciated.', 5.84, 40.47, {
    align: 'left',
    maxWidth: 42.33
  });

  // Name text with wrapping
  let fontSize = 18.25;
  const nameText = nickname
    ? `${nickname}`
    : `${firstName || ''}${firstName && lastName ? ' ' : ''}${lastName || ''}`;

  doc.setFont('Montserrat-ExtraBold', 'normal');
  doc.setFontSize(fontSize);

  const maxWidth = 42.33;

  // Dynamically shrink font size if necessary
  let wrappedText = doc.splitTextToSize(nameText, maxWidth);
  while (wrappedText.length > 2 && fontSize > 8) { // Allow max 2 lines; shrink if too many
    fontSize -= 0.5;
    doc.setFontSize(fontSize);
    wrappedText = doc.splitTextToSize(nameText, maxWidth);
  }

  // Render wrapped text
  doc.text(wrappedText, 5.84, 15, { align: 'left' });
};


// Add static shape
const addGradientRectangleToPDF = doc => {
  const gradientX = (doc.internal.pageSize.getWidth() - 79.57) / 2;
  const gradientY = (doc.internal.pageSize.getHeight() - 49.91) / 2;
  doc.addImage(Border, gradientX, gradientY, 79.57, 49.91);
};

// Create jspdf document and add images and text
const generateFrontOfCard = async (user, region) => {


  const qrURL = generateQrURL(user.userId, region);
  const frontOfCard = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: [85, 55]
  });

  montserratBold;
  montserratExtaBold;
  montserratMedium;
  frontOfCard.getFontList();

  const qrImg = await generateQrImage(qrURL);
  addGradientRectangleToPDF(frontOfCard);
  addQRHolderToPDF(frontOfCard);
  addQrCodeToPDF(frontOfCard, qrImg);
  addStaticImagesToPDF(frontOfCard);
  addTextToPDF(frontOfCard, user.firstName, user.lastName, user.nickname);
  return frontOfCard;
};

// Create jspdf document for the back of the card
const generateBackOfCard = async companyLogo => {
  const backOfCard = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: [85, 55]
  });

  addGradientRectangleToPDF(backOfCard);
  await addCompanyLogoToPDF(backOfCard, companyLogo);
  return backOfCard;
};

const isValidUser = user => {
  if (!user || !user.userId || !(user.firstName || user.lastName || user.nickname)) {
    console.error('Invalid user data');
    return false;
  }

  return true;
};

const getFileName = user => {
  return user.nickname
    ? `${user.nickname}`
    : `${user.firstName && user.firstName}${user.firstName &&
        user.lastName &&
        '-'}${user.lastName && user.lastName}`;
};

// Generate QR Code Printout
export const generateBusinessCardPrintout = async (user, region, isBulk = false, users = null) => {
  try {
    const zip = new JSZip();
    const usersToProcess = isBulk ? users : [user];

    // Generate front of cards
    await Promise.all(
      usersToProcess
        .filter(user => isValidUser(user))
        .map(async user => {
          const outputFileName = getFileName(user);
          const frontOfCard = await generateFrontOfCard(user, region);
          const frontBlob = await frontOfCard.output('blob');
          zip.file(`${outputFileName}-Front.pdf`, frontBlob);
        })
    );

    // Generate back of card
    const backOfCard = await generateBackOfCard(user.companyLogo);
    const backBlob = backOfCard.output('blob');
    zip.file(`Business-Card-Back.pdf`, backBlob);

    const zipData = await zip.generateAsync({ type: 'blob' });
    const zipFileName = `${isBulk ? 'Business-Cards' : getFileName(usersToProcess[0])}.zip`;
    const a = document.createElement('a');
    a.download = zipFileName;
    a.href = URL.createObjectURL(zipData);
    a.click();
  } catch (error) {
    console.error('Error generating business card printout:', error);
  }
};
